/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {catchError, Observable, of, shareReplay, tap, throwError} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

interface CacheEntry {
    timestamp: number;
    data: Observable<any>;
}

@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private cache = new Map<string, CacheEntry>();
    private cacheDuration = 30 * 60 * 1000; // 30 minutes in milliseconds

    constructor(private http: HttpClient) {}

    // Search method with request tracking
    public getCacheable(url: string, params: any): Observable<any> {
        const fullKey = url + '|' + JSON.stringify(params);
        // Check if there is already a request or cached response for this fullKey
        const cacheEntry = this.cache.get(fullKey);
        if (cacheEntry && (Date.now() - cacheEntry.timestamp) < this.cacheDuration) {
            // Return the cached observable directly
            return cacheEntry.data;
        }

        // If no cached data or pending request, create a new request
        const request = this.http.get<any>(url, {...params, observe: 'response'}).pipe(
            shareReplay(1), // This ensures the observable is shared across subscribers
            tap((data) => {
                // Once the request completes, cache the result
                this.cache.set(fullKey, { timestamp: Date.now(), data: of(data) });
            }),
            catchError((error) => {
                // Remove the request from cache in case of error
                this.cache.delete(fullKey);
                return throwError(() => error);
            })
        );

        // Cache the observable while it's pending
        this.cache.set(fullKey, { timestamp: Date.now(), data: request });
        return request;
    }

    // Optionally: Method to clear cache (if needed)
    clearCache() {
        this.cache.clear();
    }
}
